import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  Save, 
  Trash2, 
  Euro, 
  MapPin, 
  Car, 
  Bike,
  Edit2,
  X,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Copy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sortable Class Card Component
const SortableClassCard = ({ 
  licenseClass, 
  classIndex,
  expandedClass,
  setExpandedClass,
  setDeleteConfirm,
  licenseClasses,
  setLicenseClasses,
  handleUpdateClass,
  handleComponentPriceChange,
  handleComponentUnitsChange,
  calculateTotal,
  getClassIcon,
  saving
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: licenseClass.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} className={`overflow-hidden ${isDragging ? 'shadow-2xl ring-2 ring-blue-500' : ''}`}>
      <CardHeader 
        className="cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpandedClass(expandedClass === licenseClass.id ? null : licenseClass.id)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Drag Handle */}
            <div 
              {...attributes} 
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-2 -ml-2 rounded-lg hover:bg-slate-100 transition-colors touch-none"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="w-5 h-5 text-slate-400" />
            </div>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              licenseClass.active ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'
            }`}>
              {getClassIcon(licenseClass.class_type)}
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                {licenseClass.class_name}
                {!licenseClass.active && (
                  <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                    Inaktiv
                  </span>
                )}
              </CardTitle>
              <CardDescription>{licenseClass.description}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-slate-500">Geschätzter Gesamtpreis</p>
              <p className="text-2xl font-bold text-blue-600">
                {calculateTotal(licenseClass).toLocaleString('de-DE')} €
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirm(licenseClass);
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              {expandedClass === licenseClass.id ? (
                <ChevronUp className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      
      {expandedClass === licenseClass.id && (
        <CardContent className="border-t bg-slate-50">
          <div className="space-y-6 pt-4">
            {/* Basic Settings */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center justify-between p-4 bg-white rounded-xl border">
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <p className="text-xs text-slate-500">Aktiv/Inaktiv</p>
                </div>
                <Switch
                  checked={licenseClass.active}
                  onCheckedChange={(checked) => {
                    const updated = [...licenseClasses];
                    updated[classIndex].active = checked;
                    setLicenseClasses(updated);
                  }}
                />
              </div>
              <div className="p-4 bg-white rounded-xl border">
                <Label className="text-xs text-slate-500">Mindestalter</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    value={licenseClass.min_age || ''}
                    onChange={(e) => {
                      const updated = [...licenseClasses];
                      updated[classIndex].min_age = parseInt(e.target.value) || null;
                      setLicenseClasses(updated);
                    }}
                    className="w-20"
                  />
                  <span className="text-sm text-slate-500">Jahre</span>
                </div>
              </div>
              <div className="p-4 bg-white rounded-xl border">
                <Label className="text-xs text-slate-500">Ausbildungsdauer</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    value={licenseClass.duration_weeks || ''}
                    onChange={(e) => {
                      const updated = [...licenseClasses];
                      updated[classIndex].duration_weeks = parseInt(e.target.value) || null;
                      setLicenseClasses(updated);
                    }}
                    className="w-20"
                  />
                  <span className="text-sm text-slate-500">Wochen</span>
                </div>
              </div>
              <div className="p-4 bg-white rounded-xl border">
                <Label className="text-xs text-slate-500">Klassentyp</Label>
                <p className="text-lg font-bold text-slate-900 mt-1">{licenseClass.class_type}</p>
              </div>
            </div>

            {/* Cost Components */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Euro className="w-5 h-5 text-blue-600" />
                Preise bearbeiten
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {licenseClass.cost_components?.map((component, compIndex) => (
                  <div 
                    key={component.name} 
                    className="p-4 bg-white rounded-xl border"
                  >
                    <Label className="text-sm font-medium text-slate-700">
                      {component.label}
                    </Label>
                    {component.is_per_unit && (
                      <span className="text-xs text-blue-600 ml-2">(pro Einheit)</span>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={component.price}
                        onChange={(e) => handleComponentPriceChange(classIndex, compIndex, e.target.value)}
                        className="flex-1"
                      />
                      <span className="text-slate-500 font-medium">€</span>
                    </div>
                    {component.is_per_unit && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-slate-500">Standard</Label>
                            <Input
                              type="number"
                              value={component.default_units}
                              onChange={(e) => handleComponentUnitsChange(classIndex, compIndex, 'default_units', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Minimum</Label>
                            <Input
                              type="number"
                              value={component.min_units}
                              onChange={(e) => handleComponentUnitsChange(classIndex, compIndex, 'min_units', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
              <h4 className="font-semibold text-slate-900 mb-2">Kostenvorschau</h4>
              <p className="text-sm text-slate-600 mb-3">
                Beispielrechnung bei {licenseClass.cost_components?.find(c => c.name === 'uebungsfahrt')?.default_units || 10} Übungs- & {licenseClass.cost_components?.find(c => c.name === 'sonderfahrt')?.default_units || 12} Sonderfahrten
              </p>
              <div className="flex flex-wrap gap-4">
                {licenseClass.cost_components?.map((comp) => (
                  <div key={comp.name} className="text-sm">
                    <span className="text-slate-600">{comp.label}: </span>
                    <span className="font-semibold">
                      {comp.is_per_unit 
                        ? (comp.price * comp.default_units).toLocaleString('de-DE', {minimumFractionDigits: 2})
                        : comp.price.toLocaleString('de-DE', {minimumFractionDigits: 2})
                      } €
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-blue-200">
                <span className="text-lg font-bold text-blue-700">
                  Gesamt: {calculateTotal(licenseClass).toLocaleString('de-DE', {minimumFractionDigits: 2})} €
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setExpandedClass(null)}
              >
                Abbrechen
              </Button>
              <Button
                onClick={() => handleUpdateClass(licenseClass)}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Speichern...' : 'Änderungen speichern'}
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const AdminPricing = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [licenseClasses, setLicenseClasses] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [newClassType, setNewClassType] = useState('');
  const [expandedClass, setExpandedClass] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }
    
    const oldIndex = licenseClasses.findIndex(c => c.id === active.id);
    const newIndex = licenseClasses.findIndex(c => c.id === over.id);
    
    const reordered = arrayMove(licenseClasses, oldIndex, newIndex);
    setLicenseClasses(reordered);
    
    // Save new order to backend
    try {
      const token = localStorage.getItem('adminToken');
      const items = reordered.map((item, index) => ({
        id: item.id,
        order: index
      }));
      
      await axios.put(`${API_URL}/api/license-classes/reorder`, { items }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Reihenfolge gespeichert!');
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Fehler beim Speichern der Reihenfolge');
      // Revert on error
      fetchLocationClasses(selectedLocation);
    }
  }, [licenseClasses, selectedLocation]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchLocationClasses(selectedLocation);
    }
  }, [selectedLocation]);

  const fetchData = async () => {
    try {
      const [locationsRes, classTypesRes] = await Promise.all([
        axios.get(`${API_URL}/api/locations`),
        axios.get(`${API_URL}/api/license-class-types`)
      ]);
      
      setLocations(locationsRes.data);
      setClassTypes(classTypesRes.data);
      
      if (locationsRes.data.length > 0 && !selectedLocation) {
        setSelectedLocation(locationsRes.data[0].slug);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationClasses = async (locationSlug) => {
    try {
      const response = await axios.get(`${API_URL}/api/license-classes`);
      const locationClasses = response.data.filter(c => c.location_slug === locationSlug);
      setLicenseClasses(locationClasses);
    } catch (error) {
      console.error('Error fetching license classes:', error);
      toast.error('Fehler beim Laden der Führerscheinklassen');
    }
  };

  const handleAddClass = async () => {
    if (!newClassType || !selectedLocation) return;
    
    const classType = classTypes.find(ct => ct.type === newClassType);
    if (!classType) return;
    
    const locationName = locations.find(l => l.slug === selectedLocation)?.name || selectedLocation;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const newClass = {
        class_type: classType.type,
        class_name: classType.name,
        location_slug: selectedLocation,
        location_name: locationName,
        active: true,
        description: classType.description,
        min_age: classType.min_age,
        duration_weeks: 8,
        cost_components: classType.default_components.map(c => ({
          name: c.name,
          label: c.label,
          price: c.price,
          is_per_unit: c.is_per_unit || false,
          default_units: c.default_units || 1,
          min_units: c.min_units || 0,
          max_units: c.max_units || 100
        }))
      };
      
      await axios.post(`${API_URL}/api/license-classes`, newClass, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${classType.name} wurde hinzugefügt!`);
      setShowAddDialog(false);
      setNewClassType('');
      fetchLocationClasses(selectedLocation);
    } catch (error) {
      console.error('Error adding class:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen der Klasse');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateClass = async (classData) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API_URL}/api/license-classes/${classData.id}`, {
        class_name: classData.class_name,
        active: classData.active,
        description: classData.description,
        min_age: classData.min_age,
        duration_weeks: classData.duration_weeks,
        cost_components: classData.cost_components
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Änderungen gespeichert!');
      fetchLocationClasses(selectedLocation);
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClass = async (classId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/api/license-classes/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Klasse gelöscht!');
      setDeleteConfirm(null);
      fetchLocationClasses(selectedLocation);
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleComponentPriceChange = (classIndex, componentIndex, value) => {
    const updated = [...licenseClasses];
    updated[classIndex].cost_components[componentIndex].price = parseFloat(value) || 0;
    setLicenseClasses(updated);
  };

  const handleComponentUnitsChange = (classIndex, componentIndex, field, value) => {
    const updated = [...licenseClasses];
    updated[classIndex].cost_components[componentIndex][field] = parseInt(value) || 0;
    setLicenseClasses(updated);
  };

  const calculateTotal = (licenseClass) => {
    if (!licenseClass?.cost_components) return 0;
    return licenseClass.cost_components.reduce((sum, comp) => {
      if (comp.is_per_unit) {
        return sum + (comp.price * comp.default_units);
      }
      return sum + comp.price;
    }, 0);
  };

  const getClassIcon = (classType) => {
    if (['A', 'A1', 'A2', 'AM'].includes(classType)) {
      return <Bike className="w-5 h-5" />;
    }
    return <Car className="w-5 h-5" />;
  };

  // Show all class types in dropdown (backend will prevent duplicates)
  const availableClassTypes = classTypes;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pricing">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Preise verwalten</h1>
          <p className="text-slate-600">Führerscheinklassen und Preise pro Standort verwalten</p>
        </div>
      </div>

      {/* Location Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Standort auswählen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label>Standort</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger data-testid="location-select">
                  <SelectValue placeholder="Standort wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(location => (
                    <SelectItem key={location.slug} value={location.slug}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={() => setShowAddDialog(true)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="add-class-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neue Klasse hinzufügen
            </Button>
          </div>
          {availableClassTypes.length === 0 && (
            <p className="text-sm text-amber-600 mt-2">
              Keine Führerscheinklassen verfügbar.
            </p>
          )}
        </CardContent>
      </Card>

      {/* License Classes List */}
      <div className="space-y-4">
        {licenseClasses.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Keine Klassen vorhanden</h3>
              <p className="text-slate-500 mb-4">
                Für diesen Standort wurden noch keine Führerscheinklassen angelegt.
              </p>
              <Button onClick={() => setShowAddDialog(true)} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Erste Klasse hinzufügen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-sm text-slate-500 flex items-center gap-2">
              <GripVertical className="w-4 h-4" />
              Ziehen Sie die Karten, um die Reihenfolge zu ändern
            </p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={licenseClasses.map(c => c.id)}
                strategy={verticalListSortingStrategy}
              >
                {licenseClasses.map((licenseClass, classIndex) => (
                  <SortableClassCard
                    key={licenseClass.id}
                    licenseClass={licenseClass}
                    classIndex={classIndex}
                    expandedClass={expandedClass}
                    setExpandedClass={setExpandedClass}
                    setDeleteConfirm={setDeleteConfirm}
                    licenseClasses={licenseClasses}
                    setLicenseClasses={setLicenseClasses}
                    handleUpdateClass={handleUpdateClass}
                    handleComponentPriceChange={handleComponentPriceChange}
                    handleComponentUnitsChange={handleComponentUnitsChange}
                    calculateTotal={calculateTotal}
                    getClassIcon={getClassIcon}
                    saving={saving}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </>
        )}
      </div>

      {/* Add Class Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Neue Führerscheinklasse hinzufügen</DialogTitle>
            <DialogDescription>
              Wählen Sie eine Klasse für den Standort "{locations.find(l => l.slug === selectedLocation)?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Führerscheinklasse</Label>
              <Select value={newClassType} onValueChange={setNewClassType}>
                <SelectTrigger className="mt-1" data-testid="class-type-select">
                  <SelectValue placeholder="Klasse wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {availableClassTypes.map(ct => (
                    <SelectItem key={ct.type} value={ct.type}>
                      <div className="flex items-center gap-2">
                        {getClassIcon(ct.type)}
                        <div>
                          <span className="font-medium">{ct.name}</span>
                          <span className="text-slate-500 ml-2 text-sm">- ab {ct.min_age} Jahre</span>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {newClassType && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  {classTypes.find(ct => ct.type === newClassType)?.description}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Die Standardpreise werden automatisch eingetragen und können danach angepasst werden.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleAddClass} 
              disabled={!newClassType || saving}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              {saving ? 'Hinzufügen...' : 'Klasse hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Klasse löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie "{deleteConfirm?.class_name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteClass(deleteConfirm?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPricing;
